import { html, nothing } from "lit";
import type { FleetComposerMode } from "./fleet-composer-mode.ts";
import { PLAN_IMPLEMENT_HINTS } from "../control-hints.ts";
import { extractText } from "./message-extract.ts";
import { isAssistantMessage, isUserMessage, readTranscriptId } from "./message-transcript.ts";

/** Minimum assistant plan text length before offering implementation. */
export const PLAN_IMPLEMENT_MIN_CHARS = 120;

export const PLAN_IMPLEMENT_PROMPT =
  "Implement the plan you outlined in your last message. Follow the steps and acceptance criteria exactly unless I say otherwise. Use fleet_todo_write to track progress. Use fleet_ask_user only before irreversible or scope-fork choices — not for routine ambiguity.";

export type PlanImplementOfferEval = {
  show: boolean;
  fingerprint: string;
  planPreview: string;
};

export type PlanImplementOfferParams = {
  sessionKey: string;
  composerMode: FleetComposerMode;
  messages: unknown[];
  runActive: boolean;
  dismissedFingerprint: string | null;
};

function lastUserAssistantPair(messages: unknown[]): {
  userIndex: number;
  assistantIndex: number;
  assistant: unknown;
} | null {
  let lastUserIndex = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (isUserMessage(messages[i])) {
      lastUserIndex = i;
      break;
    }
  }
  if (lastUserIndex < 0) {
    return null;
  }
  for (let i = messages.length - 1; i > lastUserIndex; i--) {
    if (isAssistantMessage(messages[i])) {
      return {
        userIndex: lastUserIndex,
        assistantIndex: i,
        assistant: messages[i],
      };
    }
  }
  return null;
}

export function planImplementFingerprint(
  sessionKey: string,
  assistantMessage: unknown,
  assistantIndex: number,
): string {
  const session = sessionKey.trim();
  const id = readTranscriptId(assistantMessage);
  if (id) {
    return `${session}:id:${id}`;
  }
  return `${session}:idx:${assistantIndex}`;
}

export function evaluatePlanImplementOffer(
  params: PlanImplementOfferParams,
): PlanImplementOfferEval {
  const empty: PlanImplementOfferEval = { show: false, fingerprint: "", planPreview: "" };
  if (params.composerMode !== "plan" || params.runActive) {
    return empty;
  }
  const pair = lastUserAssistantPair(params.messages);
  if (!pair) {
    return empty;
  }
  const planText = (extractText(pair.assistant) ?? "").trim();
  if (planText.length < PLAN_IMPLEMENT_MIN_CHARS) {
    return empty;
  }
  const fingerprint = planImplementFingerprint(
    params.sessionKey,
    pair.assistant,
    pair.assistantIndex,
  );
  if (params.dismissedFingerprint === fingerprint) {
    return { show: false, fingerprint, planPreview: planText };
  }
  const preview =
    planText.length > 200 ? `${planText.slice(0, 199).replace(/\s+/g, " ")}…` : planText;
  return { show: true, fingerprint, planPreview: preview };
}

export function clearPlanImplementDismissForSession(
  dismissed: Record<string, string>,
  sessionKey: string,
): Record<string, string> {
  const key = sessionKey.trim();
  if (!key || !(key in dismissed)) {
    return dismissed;
  }
  const next = { ...dismissed };
  delete next[key];
  return next;
}

export function dismissPlanImplementFingerprint(
  dismissed: Record<string, string>,
  sessionKey: string,
  fingerprint: string,
): Record<string, string> {
  const key = sessionKey.trim();
  if (!key || !fingerprint) {
    return dismissed;
  }
  return { ...dismissed, [key]: fingerprint };
}

export type PlanImplementOfferProps = {
  planPreview?: string;
  onImplement: () => void;
  onDismiss: () => void;
};

export function renderPlanImplementOffer(props: PlanImplementOfferProps) {
  return html`
    <div class="chat-plan-implement-offer callout info" role="status" aria-live="polite">
      <p class="chat-plan-implement-offer__text">
        <strong>Plan ready.</strong> Review the reply above, then implement when you are satisfied.
        ${
          props.planPreview
            ? html`
                <span class="chat-plan-implement-offer__preview muted">${props.planPreview}</span>
              `
            : nothing
        }
      </p>
      <div class="chat-plan-implement-offer__actions">
        <button
          type="button"
          class="btn primary"
          title=${PLAN_IMPLEMENT_HINTS.implement}
          @click=${props.onImplement}
        >
          Implement
        </button>
        <button
          type="button"
          class="btn"
          title=${PLAN_IMPLEMENT_HINTS.dismiss}
          @click=${props.onDismiss}
        >
          Dismiss
        </button>
      </div>
    </div>
  `;
}
